import { Badge } from 'components/VehicleBadges/types';
import { BasicInfo } from 'components/Vdp/VehicleInformation';
import ApiResponse from 'types/Apis/VehicleApiResponse';
import { ExperienceApiResponse } from 'types/Apis/VehicleWithExperience';
import LeadTrackingParams from 'types/Apis/LeadTrackingParams';
import { formatMoney } from 'accounting';
import { getVdpBadges } from './BadgeDisplayLogic';
import BuyerIntelligencePresenter from '../BuyerIntelligencePresenter';
import noImages from './no-images.png';

export default class VehicleViewModel {
  private apiData: ApiResponse;

  private experienceData: ExperienceApiResponse;

  constructor(apiData: ApiResponse, experienceData: ExperienceApiResponse) {
    this.apiData = apiData;
    this.experienceData = experienceData;
  }

  get vdpUrl(): string {
    return this.apiData.vdp_url;
  }

  get images(): string[] {
    if (this.apiData.has_photos) {
      return this.apiData.photo_urls;
    }

    return [noImages];
  }

  get recentPriceDrop(): boolean {
    return this.apiData.recent_price_drop || false;
  }

  get badges(): Badge[] {
    return getVdpBadges({
      vehicleIsHot: this.apiData.is_hot,
      vehicleHasRecentPriceDrop: this.recentPriceDrop,
      vehicleWasRecentlyListed: this.apiData.time_on_market === 1,
    });
  }

  get make(): string {
    return this.apiData.make;
  }

  get model(): string {
    return this.apiData.model;
  }

  get year(): number {
    return this.apiData.year;
  }

  get trim(): string | null {
    return this.apiData.trim;
  }

  get price(): number {
    return this.apiData.price;
  }

  get priceFormatted(): string | null {
    return formatMoney(this.apiData.price, '$', 0);
  }

  get mileage(): number | null {
    return this.apiData.mileage;
  }

  get city(): string {
    return this.apiData.city;
  }

  get state(): string {
    return this.apiData.state;
  }

  get acceptsLeads(): boolean {
    return this.apiData.accepts_leads;
  }

  get phoneNumber(): string | null {
    return this.experienceData.phone || this.apiData.phone;
  }

  get vin(): string {
    return this.apiData.vin;
  }

  get isHot(): boolean {
    return this.apiData.is_hot;
  }

  get timeOnMarket(): number | undefined | null {
    return this.apiData.time_on_market;
  }

  get buyerIntelligence(): BuyerIntelligencePresenter {
    return new BuyerIntelligencePresenter(this.apiData);
  }

  get relativePriceDiff(): number {
    return this.apiData.relative_price_difference;
  }

  get basicInfo(): BasicInfo[] {
    return [
      { label: 'trim', data: this.apiData.trim },
      { label: 'transmission', data: this.apiData.transmission },
      { label: 'engine', data: this.apiData.engine_type },
      { label: 'drivetrain', data: this.apiData.driveline },
      { label: 'cabin', data: this.apiData.cabin },
      { label: 'bed', data: this.apiData.bed },
      { label: 'rearWheel', data: this.apiData.rear_wheel },
      { label: 'vin', data: this.vin },
      { label: 'fuelType', data: this.apiData.fuel_type },
      { label: 'exteriorColor', data: this.apiData.exterior_color },
      { label: 'interiorColor', data: this.apiData.interior_color },
      { label: 'condition', data: this.apiData.condition },
      { label: 'mileage', data: this.apiData.mileage },
      { label: 'gasMileage', data: this.apiData.mpg },
      { label: 'bodyType', data: this.apiData.body_type },
      { label: 'doors', data: this.apiData.door_count },
      { label: 'stockNumber', data: this.apiData.remote_sku },
    ];
  }

  get interiorFeatures(): string[] {
    return this.apiData.interior_features;
  }

  get exteriorFeatures(): string[] {
    return this.apiData.exterior_features;
  }

  get safetyFeatures(): string[] {
    return this.apiData.safety_features;
  }

  get otherFeatures(): string[] {
    return this.apiData.other_features;
  }

  get moreFeatures(): string[] {
    return this.apiData.features;
  }

  get dealerName(): string {
    return this.experienceData.dealer_name || this.apiData.dealer_name;
  }

  get mapImageUrl(): string {
    return this.apiData.map_urls.mdw;
  }

  get zip(): string {
    return this.apiData.zip;
  }

  get address(): string {
    return this.apiData.address;
  }

  get mapUrl(): string {
    const url = new URL('https://www.google.com/maps/search/?api=1');
    const params = new URLSearchParams({
      api: '1',
      query: this.address,
    });
    url.search = params.toString();

    return url.toString();
  }

  get website(): string | null {
    return this.apiData.clickoff_url;
  }

  get description(): string | null {
    return this.apiData.description;
  }

  get transferAvailable(): boolean {
    return Boolean(this.experienceData.is_transfer_available);
  }

  get isRegional(): boolean {
    return typeof this.experienceData.is_local_experience === 'undefined'
      ? false
      : !this.experienceData.is_local_experience;
  }

  get location(): string {
    return this.experienceData.display_location || `${this.apiData.city}, ${this.apiData.state}`;
  }

  get experienceHeadline(): string | undefined {
    return this.experienceData.experience_headline;
  }

  get experienceBody(): string | undefined {
    return this.experienceData.experience_body;
  }

  get trackingParams(): LeadTrackingParams {
    return this.apiData.tracking_params;
  }

  get experience(): string {
    return this.apiData.experience;
  }

  get dealerDisclaimer(): string | null {
    return this.apiData.dealer_disclaimer || null;
  }

  get listingId(): number {
    return this.apiData.listing_id;
  }
}
